<template>
  <div class="field-group">
    <label :for="id">{{ label }}</label>

    <div class="input-wrap" :class="{ 'has-toggle': isPasswordField }">
      <input
        :id="id"
        :value="modelValue"
        :type="resolvedType"
        :inputmode="inputmode"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :class="{ 'is-error': !!errorMessage }"
        @input="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur')"
      />

      <button
        v-if="isPasswordField"
        class="password-toggle"
        type="button"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
        @click="togglePassword"
      >
        {{ showPassword ? 'Hide' : 'Show' }}
      </button>
    </div>

    <small v-if="errorMessage" class="error-text">{{ errorMessage }}</small>
    <small v-else-if="hint">{{ hint }}</small>
  </div>
</template>

<script>
export default {
  name: 'BaseInput',
  props: {
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    inputmode: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: undefined,
    },
  },
  emits: ['update:modelValue', 'blur'],
  data() {
    return {
      showPassword: false,
    }
  },
  computed: {
    isPasswordField() {
      return this.type === 'password'
    },
    resolvedType() {
      if (!this.isPasswordField) return this.type
      return this.showPassword ? 'text' : 'password'
    },
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword
    },
  },
}
</script>

<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
}

.field-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #36442c;
}

.input-wrap {
  position: relative;
}

.input-wrap.has-toggle input {
  padding-right: 72px;
}

.field-group input {
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1.5px solid #b8c2aa;
  background: #ffffff;
  font-size: 16px;
  color: #26311d;
  outline: none;
  box-shadow: 0 6px 14px rgba(68, 85, 38, 0.05);
}

.field-group input::placeholder {
  color: #8b9485;
}

.field-group input:focus {
  border-color: #fcc415;
  box-shadow: 0 0 0 4px rgba(252, 196, 21, 0.18),
    0 9px 18px rgba(68, 85, 38, 0.08);
}

.field-group input.is-error {
  border-color: #cf7d7d;
  box-shadow: 0 6px 14px rgba(207, 125, 125, 0.08);
}

.field-group input.is-error:focus {
  border-color: #cf7d7d;
  box-shadow: 0 0 0 4px rgba(207, 125, 125, 0.14),
    0 9px 18px rgba(207, 125, 125, 0.12);
}

.field-group input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #5f6a56;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 4px 6px;
}

.password-toggle:focus {
  outline: none;
  color: #445526;
}

.field-group small {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #5d6657;
}

.error-text {
  display: block;
  margin-top: 8px;
  color: #b05b5b !important;
  font-size: 12px;
  line-height: 1.5;
}
</style>
